import React, { useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FIELDS as FF } from "@/lib/fields";
import { Info } from "@/components/Info";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const LOAN_FIELDS = [FF.LOAN_AMOUNT, FF.AGREEMENT_VALUE];

// A single inline-editable field with save / cancel.
// type: "text" | "number" | "date" | "select" | "multiselect" | "textarea"
export function EditableField({
  label, value, fieldId, type = "text", options = [], editable = true,
  onSave, render, testid, hint,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [sanity, setSanity] = useState(null);

  const start = () => { setDraft(value ?? ""); setEditing(true); };
  const cancel = () => { setSanity(null); setEditing(false); };

  const persist = async (out) => {
    setSaving(true);
    try {
      const ok = await onSave(fieldId, out);
      if (ok !== false) { setSanity(null); setEditing(false); }
    } finally {
      setSaving(false);
    }
  };

  const commit = async () => {
    let out = draft;
    if (type === "number") out = draft === "" ? null : Number(draft);
    if (LOAN_FIELDS.includes(fieldId) && typeof out === "number" && out > 0 && out < 100000) {
      setSanity(out);
      return;
    }
    await persist(out);
  };

  const displayValue = () => {
    if (render) return render(value);
    if (value === null || value === undefined || value === "") return <span className="text-[#C5C4C4]">—</span>;
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
  };

  if (!editing) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-medium uppercase text-[#6B7280] flex items-center gap-1" style={{ letterSpacing: "0.06em" }}>{label}{hint && <Info k={hint} />}</span>
        <div
          className={`group relative flex items-center justify-between p-2 rounded-lg border border-transparent min-h-[36px] ${
            editable ? "hover:bg-[#F6F5EF] hover:border-[#E2E2E2] cursor-pointer" : ""
          }`}
          onClick={editable ? start : undefined}
          data-testid={testid || `field-${fieldId}`}
        >
          <span className="text-sm text-[#333333]">{displayValue()}</span>
          {editable && (
            <Pencil className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase text-[#6B7280]" style={{ letterSpacing: "0.06em" }}>{label}</span>
      <div className="flex items-center gap-2 p-1.5 rounded-lg bg-white border border-[#FF4802] ring-2 ring-[#FF4802]/20">
        {type === "select" ? (
          <Select value={draft || ""} onValueChange={setDraft}>
            <SelectTrigger className="h-8 text-sm" data-testid={`edit-select-${fieldId}`}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {options.map((o) => (
                <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : type === "multiselect" ? (
          <MultiSelectEditor value={draft} options={options} onChange={setDraft} fieldId={fieldId} />
        ) : type === "textarea" ? (
          <Textarea value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} className="text-sm min-h-[80px]" data-testid={`edit-input-${fieldId}`} />
        ) : (
          <Input
            type={type === "date" ? "date" : type === "number" ? "number" : "text"}
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 text-sm"
            data-testid={`edit-input-${fieldId}`}
          />
        )}
        <button onClick={commit} disabled={saving} data-testid={`save-field-${fieldId}`}
          className="p-1.5 rounded-lg bg-[#FF4802] text-white hover:bg-[#E2521B] disabled:opacity-50 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button onClick={cancel} disabled={saving} data-testid={`cancel-field-${fieldId}`}
          className="p-1.5 rounded-lg bg-[#F7F7F7] text-[#6B7280] hover:bg-[#E2E2E2] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      {sanity !== null && (
        <div className="mt-1 rounded-lg border border-[#F5B71D]/50 bg-[#F5B71D]/10 p-2 text-xs text-[#92600a]" data-testid={`loan-sanity-${fieldId}`}>
          Did you mean <b>₹{sanity} Cr</b> (₹{(sanity * 10000000).toLocaleString("en-IN")})?
          <div className="flex gap-2 mt-1.5">
            <button onClick={() => persist(sanity * 10000000)} disabled={saving} data-testid={`sanity-convert-${fieldId}`}
              className="px-2.5 py-1 rounded-md bg-[#FF4802] text-white font-medium disabled:opacity-50">Yes, ₹{sanity} Cr</button>
            <button onClick={() => persist(sanity)} disabled={saving} data-testid={`sanity-keep-${fieldId}`}
              className="px-2.5 py-1 rounded-md border border-[#E2E2E2] bg-white text-[#333333]">No, keep ₹{sanity}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelectEditor({ value, options, onChange, fieldId }) {
  const arr = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (o) => {
    if (arr.includes(o)) onChange(arr.filter((x) => x !== o));
    else onChange([...arr, o]);
  };
  return (
    <div className="flex flex-wrap gap-1.5 max-w-full" data-testid={`edit-multi-${fieldId}`}>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => toggle(o)}
          className={`px-2 py-1 rounded-full text-xs border transition-colors ${
            arr.includes(o)
              ? "bg-[#FF4802] text-white border-[#FF4802]"
              : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
